import React, { useState } from "react";
import { Grid, Button, CircularProgress, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const CashOrderReport = ({ customers }) => {
  const [loadingReport, setLoadingReport] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
  });

  const generateReport = async (exportType = 'html') => {
    const params = new URLSearchParams();

    if (filters.dateFrom) {
      params.append('date_from', filters.dateFrom.format("YYYY-MM-DD"));
    }

    if (filters.dateTo) {
      params.append('date_to', filters.dateTo.format("YYYY-MM-DD"));
    }

    const queryString = params.toString();

    setLoadingReport(true);
    try {
      const response = await api.get(
        `/reports/cash-order-report${queryString ? `?${queryString}` : ''}`
      );

      if (response.data && response.data.status) {
        const dateFromStr = filters.dateFrom ? filters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = filters.dateTo ? filters.dateTo.format("YYYY-MM-DD") : "All";

        if (exportType === 'excel') {
          const excelData = response.data.report.map(order => ({
            "Style": order.style,
            "Item": order.item,
            "Party": order.party,
            "Quantity": order.quantity,
            "Rate": order.rate,
            "Amount": order.amount,
            "Received": order.received,
            "Balance":order?.credit,
            "Detail": order.detail,
            "Paid": order.paid,
            "Ledger": order.ledger

          }));

          exportToExcel(excelData, `Cash_Order_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }

        let totalAmount = 0;
        let totalReceived = 0;
        let totalBalance = 0;

        response.data.report.forEach(order => {
          totalAmount += Number(order.amount) || 0;
          totalReceived += Number(order.received) || 0;
          totalBalance += Number(order.credit) || 0;
        });

        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Cash Sheet</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
             h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin: 20px; }
              .t {margin:20px;}
              .report-filters { margin: 20px; font-size: 14px; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
              .totals-row th {
                background-color: #ffffff !important;
                color: #000;
                font-weight: bold;
              }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Cash Sheet</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
               <p class="t">Sheet # = ${response.data?.sh_no || 'N/A'}</p>
              <p class="t">Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <table>
              <thead>
                <tr class="totals-row">
                  
                  <th colspan="6" style="text-align:right;">${totalAmount.toFixed(2)}</th>
                  <th>${totalReceived.toFixed(2)}</th>
                  <th>${totalBalance.toFixed(2)}</th>
                  <th colspan="2"></th>
                </tr>
                <tr>
                  <th>Style</th>
                  <th>Item</th>
                  <th>Customer</th>
                  <th>Quantity</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th>Received</th>
                  <th>Balance</th>

                  <th>Detail</th>
                 
                  <th>Ledger</th>

                </tr>
              </thead>
              <tbody>
        `;

        response.data.report.forEach(order => {
          htmlContent += `
            <tr>
              <td>${order.style}</td>
              <td>${order.item}</td>
              <td>${order.party}</td>
              <td>${order.quantity}</td>
              <td>${order.rate}</td>
              <td>${order.amount}</td>
              <td>${order.received}</td>
              <td>${order.credit}</td>

              <td>${order.detail}</td>
              
              <td>${order.ledger}</td>
            </tr>
          `;
        });

        htmlContent += `
              </tbody>
            </table>
            
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;

        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `Cash_Order_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }

        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingReport(false);
          return;
        }

        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }

        reportWindow.document.write(htmlContent);
        reportWindow.document.close();

        window.addEventListener('message', function (event) {
          if (event.data.type === 'exportPDF' && event.data.reportType === 'cashOrder') {
            generateReport('pdf');
          } else if (event.data.type === 'exportExcel' && event.data.reportType === 'cashOrder') {
            generateReport('excel');
          }
        });

        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error generating cash order report:", error);
      toast.error("Failed to generate cash order report");
    } finally {
      setLoadingReport(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('date_from', filters.dateFrom.format("YYYY-MM-DD"));
      if (filters.dateTo) params.append('date_to', filters.dateTo.format("YYYY-MM-DD"));
      
      const queryString = params.toString();
      
      const response = await api.get(`/reports/cash-order-report${queryString ? `?${queryString}` : ''}`);
      
      if (response.data && response.data.status) {
        const dateFromStr = filters.dateFrom ? filters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = filters.dateTo ? filters.dateTo.format("YYYY-MM-DD") : "All";

        let totalAmount = 0;
        let totalReceived = 0;
        let totalBalance = 0;

        response.data.report.forEach(order => {
          totalAmount += Number(order.amount) || 0;
          totalReceived += Number(order.received) || 0;
          totalBalance += Number(order.credit) || 0;
        });

        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Cash Sheet</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #1abc9c; }
              table { border-collapse: collapse; width: 100%; margin-top: 20px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
              .totals-row th {
                background-color: #ffffff !important;
                color: #000;
                font-weight: bold;
              }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Cash Order Report</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p class="t">Sheet # = ${response.data?.sh_no || 'N/A'}</p>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            <p class="t">Sheet # = ${response.data?.sh_no || 'N/A'}</p>
              </div>
            
            <table>
              <thead>
                <tr class="totals-row">
                  <th colspan="5" style="text-align:right;">Total</th>
                  <th>${totalAmount.toFixed(2)}</th>
                  <th>${totalReceived.toFixed(2)}</th>
                  <th>${totalBalance.toFixed(2)}</th>
                  <th colspan="2"></th>
                </tr>
                <tr>
                  <th>Style</th>
                  <th>Item</th>
                  <th>Customer</th>
                  <th>Quantity</th>
                  <th>Rate</th>
                    <th>Amount</th>
                  <th>Received</th>
                  <th>Balance</th>

                  <th>Detail</th>
                 
                  <th>Ledger</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        // Add rows for each order
        response.data.report.forEach(order => {
          htmlContent += `
            <tr>
              <td>${order.style}</td>
              <td>${order.item}</td>
              <td>${order.party}</td>
              <td>${order.quantity}</td>
              <td>${order.rate}</td>
              <td>${order.amount}</td>
              <td>${order.received}</td>
              <td>${order.credit}</td>
              <td>${order.detail}</td>
             
              <td>${order.ledger}</td>
            </tr>
          `;
        });
        
        htmlContent += `
              </tbody>
            </table>
          </body>
          </html>
        `;
        
        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);
        
        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Cash_Order_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={5}>
        <DatePicker
          label="From Date"
          value={filters.dateFrom}
          onChange={(newValue) => setFilters({ ...filters, dateFrom: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={5}>
        <DatePicker
          label="To Date"
          value={filters.dateTo}
          onChange={(newValue) => setFilters({ ...filters, dateTo: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>

      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateReport('html')}
            disabled={loadingReport || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{
              height: "38px",
              backgroundColor: colors.primary,
              '&:hover': {
                backgroundColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            {loadingReport ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('pdf')}
            disabled={loadingReport || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('excel')}
            disabled={loadingReport || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('email')}
            disabled={loadingReport || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default CashOrderReport;
